import { useTranslation } from 'react-i18next';
import { Box, Palette } from '@mui/material';

import BarGraph from './BarGraph';
import { RESOLUTION_CHART_COLORS } from 'src/components/dashboard-pages/solve-insights/common/ResolutionInsightsCards';
import {
  colorMap,
  getReadableNoResponseString,
} from 'src/components/dashboard-pages/solve-insights/helpers';
import type { ResolutionRateBarGraphResponse } from 'src/services/insight-cards/types';
import {
  BarGraphResponse,
  CommonCsatResponse,
  EmailNoResponseResponse,
  QuickFeedbackResponse,
  TopicsEngagementResponse,
  TopicsRelevanceResponse,
} from 'src/services/insights/types';

export const BarGraphWrapper = ({
  data,
  isComparing = false,
  palette,
  showTooltip = false,
}: {
  data:
    | BarGraphResponse
    | CommonCsatResponse
    | TopicsRelevanceResponse
    | TopicsEngagementResponse
    | QuickFeedbackResponse
    | EmailNoResponseResponse
    | ResolutionRateBarGraphResponse;
  isComparing?: boolean;
  palette: Palette;
  showTooltip?: boolean;
}) => {
  const { t } = useTranslation('solve-insights');
  const { bar, type } = data;
  const colorMapping = colorMap(palette);
  let colors;
  switch (type) {
    case 'insights_csat_articles':
      colors = [
        colorMapping.csat[5],
        colorMapping.csat[4],
        colorMapping.csat[3],
        colorMapping.csat[2],
        colorMapping.csat[1],
      ];
      if (bar) {
        return (
          <Box
            sx={{
              '* > path': {
                clipPath: 'inset(0% 0% 1px 0% round 6px)',
              },
            }}
          >
            <BarGraph
              colors={colors}
              containerProps={{
                style: {
                  height: '160px',
                  width: '300px',
                },
              }}
              data={bar}
              disableTooltip={!showTooltip}
              disableYAxisLabel
              disableYGridLines
              horizontal
              showDataLabel
            />
          </Box>
        );
      }
    case 'insights_csat_common':
      colors = [
        colorMapping.csat[5],
        colorMapping.csat[4],
        colorMapping.csat[3],
        colorMapping.csat[2],
        colorMapping.csat[1],
      ];
      if (bar) {
        return (
          <Box
            sx={{
              '* > path': {
                clipPath: 'inset(0% 0% 1px 0% round 6px)',
              },
            }}
          >
            <BarGraph
              colors={colors}
              containerProps={{
                style: {
                  height: isComparing ? '220px' : '160px',
                  width: '280px',
                },
              }}
              data={bar}
              disableTooltip={!showTooltip}
              disableYAxisLabel
              disableYGridLines
              horizontal
              isComparing={isComparing}
              showDataLabel
              spacing={[10, 0, 0, 0]}
            />
          </Box>
        );
      }
    case 'insights_workflow_csat':
      colors = [
        colorMapping.csat[5],
        colorMapping.csat[4],
        colorMapping.csat[3],
        colorMapping.csat[2],
        colorMapping.csat[1],
      ];
      if (bar) {
        return (
          <Box
            sx={{
              '* > path': {
                clipPath: 'inset(0% 0% 1px 0% round 6px)',
              },
            }}
          >
            <BarGraph
              colors={colors}
              containerProps={{
                style: {
                  height: isComparing ? '220px' : '160px',
                  width: '280px',
                },
              }}
              data={bar}
              disableTooltip={!showTooltip}
              disableYAxisLabel
              disableYGridLines
              horizontal
              isComparing={isComparing}
              showDataLabel
            />
          </Box>
        );
      }
    case 'email_no_response_insight':
      colors = [
        colorMapping.csat[5],
        colorMapping.csat[4],
        colorMapping.csat[3],
        colorMapping.csat[2],
        colorMapping.csat[1],
      ];
      if (bar) {
        const barCount = bar?.values?.length ?? 0;
        const transformDataLabel = bar?.values.map(item => {
          return {
            ...item,
            label: getReadableNoResponseString(item.label),
          };
        });

        const transformedBar = {
          ...data?.bar,
          values: transformDataLabel,
        };

        return (
          <Box
            sx={{
              '* > path': {
                clipPath: 'inset(0% 0% 1px 0% round 6px)',
              },
            }}
          >
            <BarGraph
              colors={colors}
              containerProps={{
                style: {
                  height: `${barCount * 30 + 50}px`,
                  width: '400px',
                },
              }}
              data={transformedBar}
              disableTooltip={true}
              disableYAxisLabel
              disableYGridLines
              horizontal
              showDataLabel
            />
          </Box>
        );
      }
    case 'insights_overall_relevance':
      colors = [
        colorMapping.relevance.relevant,
        colorMapping.relevance.somewhat_relevant,
        colorMapping.relevance.irrelevant,
      ];
      if (bar) {
        return (
          <Box
            sx={{
              '* > path': {
                clipPath: 'inset(0% 0% 1px 0% round 6px)',
              },
            }}
          >
            <BarGraph
              colors={colors}
              containerProps={{
                style: {
                  height: isComparing ? '150px' : '160px',
                  width: isComparing ? '100%' : '280px',
                },
              }}
              data={bar}
              disableTooltip={!showTooltip}
              disableYAxisLabel
              disableYGridLines
              horizontal
              isComparing={isComparing}
              showDataLabel
              spacing={[10, 0, 0, 0]}
            />
          </Box>
        );
      }
    case 'insights_overall_engagement':
      colors = [
        colorMapping.engagement.engaged,
        colorMapping.engagement.not_engaged,
      ];
      if (bar) {
        return (
          <Box
            sx={{
              '* > path': {
                clipPath: 'inset(0% 0% 1px 0% round 6px)',
              },
            }}
          >
            <BarGraph
              colors={colors}
              containerProps={{
                style: {
                  height: isComparing ? '100px' : '160px',
                  width: isComparing ? '100%' : '280px',
                },
              }}
              data={bar}
              disableTooltip={!showTooltip}
              disableYAxisLabel
              disableYGridLines
              horizontal
              isComparing={isComparing}
              showDataLabel
              spacing={[10, 0, 0, 0]}
            />
          </Box>
        );
      }
    // TODO - Remove insights_quick_feedback once cleaned up in BE
    case 'insights_quick_feedback':
    case 'insights_feedback':
      colors = [
        colorMapping.feedback.positive,
        colorMapping.feedback.negative,
        colorMapping.feedback.neutral,
      ];

      if (bar) {
        return (
          <Box
            sx={{
              '* > path': {
                clipPath: 'inset(0% 0% 1px 0% round 6px)',
              },
            }}
          >
            <BarGraph
              colors={colors}
              containerProps={{
                style: {
                  height: isComparing ? '110px' : '160px',
                },
              }}
              data={bar}
              disableTooltip={!showTooltip}
              disableYAxisLabel
              disableYGridLines
              horizontal
              isComparing={isComparing}
              showDataLabel
              spacing={[10, 0, 0, 0]}
            />
          </Box>
        );
      }

    case 'overview_ka_bar_chart_insight':
      colors = [colorMapping.general];
      if (bar) {
        return (
          <Box
            sx={{
              '* > path': {
                clipPath: 'inset(0% 0% 1px 0% round 6px)',
              },
              display: 'flex',
              flexDirection: 'column',
              height: 'calc(100% - 44px)',
              justifyContent: 'end',
            }}
          >
            <BarGraph
              colors={colors}
              containerProps={{
                style: {
                  height: '260px',
                  minWidth: '340px',
                  width: '100%',
                },
              }}
              data={bar}
            />
          </Box>
        );
      }
    case 'overview_csat_bar_chart_insight':
      colors = [colorMapping.general];
      if (bar) {
        return (
          <Box
            sx={{
              '* > path': {
                clipPath: 'inset(0% 0% 1px 0% round 6px)',
              },
              display: 'flex',
              flexDirection: 'column',
              height: 'calc(100% - 44px)',
              justifyContent: 'end',
            }}
          >
            <BarGraph
              colors={colors}
              containerProps={{
                style: {
                  height: '260px',
                  minWidth: '340px',
                  width: '100%',
                },
              }}
              data={bar}
            />
          </Box>
        );
      }
    case 'overview_quick_feedback_bar_chart_insight':
      colors = [
        colorMapping.feedback.positive,
        colorMapping.feedback.negative,
        colorMapping.feedback.neutral,
      ];
      if (bar) {
        return (
          <Box
            sx={{
              '* > path': {
                clipPath: 'inset(0% 0% 1px 0% round 6px)',
              },
            }}
          >
            <BarGraph
              colors={colors}
              containerProps={{
                style: {
                  height: '300px',
                  minWidth: '340px',
                  width: '100%',
                },
              }}
              data={bar}
              isStacked
              showLegend
            />
          </Box>
        );
      }
    case 'overview_resolution_rate_bar_chart_insight':
      colors = [
        RESOLUTION_CHART_COLORS.verified,
        RESOLUTION_CHART_COLORS.contained,
      ];
      if (bar) {
        return (
          <Box
            sx={{
              '* > path': {
                clipPath: 'inset(0% 0% 1px 0% round 6px)',
              },
            }}
          >
            <BarGraph
              colors={colors}
              containerProps={{
                style: {
                  height: '300px',
                  minWidth: '340px',
                  width: '100%',
                },
              }}
              data={bar}
              isStacked
              showLegend
            />
          </Box>
        );
      }
    case 'overview_relevance_bar_chart_insight':
      colors = [
        colorMapping.relevance.relevant,
        colorMapping.relevance.somewhat_relevant,
        colorMapping.relevance.irrelevant,
      ];
      if (bar) {
        return (
          <Box
            sx={{
              '* > path': {
                clipPath: 'inset(0% 0% 1px 0% round 6px)',
              },
            }}
          >
            <BarGraph
              colors={colors}
              containerProps={{
                style: {
                  height: '300px',
                  minWidth: '340px',
                  width: '100%',
                },
              }}
              data={bar}
              isStacked
              showLegend
            />
          </Box>
        );
      }
    case 'overview_engagement_bar_chart_insight':
      colors = [
        colorMapping.engagement.engaged,
        colorMapping.engagement.not_engaged,
      ];
      if (bar) {
        return (
          <Box
            sx={{
              '* > path': {
                clipPath: 'inset(0% 0% 1px 0% round 6px)',
              },
            }}
          >
            <BarGraph
              colors={colors}
              containerProps={{
                style: {
                  height: '300px',
                  minWidth: '340px',
                  width: '100%',
                },
              }}
              data={bar}
              isStacked
              showLegend
            />
          </Box>
        );
      }
    case 'overview_deflected_rate_bar_chart_insight':
      colors = [colorMapping.general];
      if (bar) {
        return (
          <Box
            sx={{
              '* > path': {
                clipPath: 'inset(0% 0% 1px 0% round 6px)',
              },
              display: 'flex',
              flexDirection: 'column',
              height: 'calc(100% - 44px)',
              justifyContent: 'end',
            }}
          >
            <BarGraph
              colors={colors}
              containerProps={{
                style: {
                  height: '260px',
                  minWidth: '340px',
                  width: '100%',
                },
              }}
              data={bar}
            />
          </Box>
        );
      }
    case 'overview_deflected_count_bar_chart_insight':
      colors = [
        colorMapping.deflection.deflected,
        colorMapping.deflection.not_deflected,
      ];
      if (bar) {
        return (
          <Box
            sx={{
              '* > path': {
                clipPath: 'inset(0% 0% 1px 0% round 6px)',
              },
            }}
          >
            <BarGraph
              colors={colors}
              containerProps={{
                style: {
                  height: '300px',
                  minWidth: '340px',
                  width: '100%',
                },
              }}
              data={bar}
              isStacked
              showLegend
            />
          </Box>
        );
      }

    default:
      return <Box>{t('charts.barGraph.notFound')}</Box>;
  }
};
